import React from "react";
import BRAND_IMAGES from "@/lib/brand";

/* Decorative gold corner bracket */
const Corner = ({ className = "", flip = "" }) => (
  <svg
    aria-hidden="true"
    className={`absolute pointer-events-none ${className}`}
    width="56"
    height="56"
    viewBox="0 0 56 56"
    fill="none"
    style={{ transform: flip }}
  >
    <path
      d="M2 22 V2 H22"
      stroke="var(--gold-deep)"
      strokeWidth="0.8"
      fill="none"
    />
    <path
      d="M2 12 V2 H12"
      stroke="var(--gold)"
      strokeWidth="0.4"
      fill="none"
      opacity="0.6"
    />
  </svg>
);

const PILLARS = [
  {
    roman: "I.",
    title: "Why doing more is keeping you stuck",
    body:
      "How hustle, over-effort and proving yourself disconnect you from the freedom you are searching for.",
  },
  {
    roman: "II.",
    title: "The quiet art of being",
    body:
      "What it actually means to do less, and how presence becomes the most powerful place you can lead your life from.",
  },
  {
    roman: "III.",
    title: "Becoming more of who you are",
    body:
      "The keys to alignment, ease and a deeper way of living, so freedom stops being something you chase and starts being something you embody.",
  },
];

const Discover = () => {
  return (
    <section
      id="discover"
      className="relative py-28 md:py-40 overflow-hidden"
      data-testid="discover-section"
      style={{
        background:
          "radial-gradient(ellipse 70% 50% at 50% 0%, #F8EDD2 0%, transparent 70%), linear-gradient(180deg, #FAF7F0 0%, #F5EFE0 100%)",
      }}
    >
      {/* Faint doorway image behind, ultra subtle */}
      <div
        className="absolute inset-0 pointer-events-none flex items-center justify-center"
        aria-hidden="true"
      >
        <img
          src={BRAND_IMAGES.heroLandscape}
          alt=""
          className="w-full max-w-5xl opacity-[0.06] object-cover"
          style={{
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 75%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 75%)",
          }}
        />
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section heading */}
        <div className="text-center max-w-3xl mx-auto fade-up">
          <p
            className="eyebrow"
            data-testid="discover-overline"
            style={{ fontSize: "0.7rem", letterSpacing: "0.4em" }}
          >
            Inside The Masterclass
          </p>
          <h2
            className="mt-7 font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight balance"
            style={{ color: "var(--ink)" }}
            data-testid="discover-headline"
          >
            What you will
            <span
              className="font-script block text-6xl md:text-8xl mt-1"
              style={{ color: "var(--gold-deep)", lineHeight: 1 }}
            >
              discover
            </span>
          </h2>
        </div>

        {/* Decorative framed paragraph */}
        <div className="mt-16 md:mt-20 relative fade-up delay-200">
          <div
            className="relative max-w-4xl mx-auto"
            data-testid="discover-paragraph-card"
            style={{
              background:
                "linear-gradient(180deg, rgba(255,255,255,0.7) 0%, rgba(255,253,247,0.55) 100%)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              padding: "4rem 2.5rem",
              boxShadow:
                "0 50px 100px -50px rgba(140, 100, 50, 0.18), inset 0 0 0 1px rgba(216, 190, 138, 0.35)",
            }}
          >
            {/* Gold corner brackets */}
            <Corner className="top-3 left-3" />
            <Corner className="top-3 right-3" flip="scaleX(-1)" />
            <Corner className="bottom-3 left-3" flip="scaleY(-1)" />
            <Corner
              className="bottom-3 right-3"
              flip="scale(-1, -1)"
            />

            {/* Opening ornamental motif */}
            <div className="flex justify-center mb-8" aria-hidden="true">
              <svg
                width="34"
                height="34"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 2 L13.2 9.5 L20.5 10.5 L14.5 14.5 L16.5 22 L12 17.5 L7.5 22 L9.5 14.5 L3.5 10.5 L10.8 9.5 Z"
                  fill="var(--gold-deep)"
                  opacity="0.85"
                />
              </svg>
            </div>

            {/* Drop-cap paragraph */}
            <p
              className="font-serif leading-[1.75] pretty text-center"
              style={{ color: "var(--ink)", fontSize: "1.4rem" }}
              data-testid="discover-paragraph"
            >
              <span
                className="font-serif italic float-left mr-3 mt-2"
                style={{
                  fontSize: "4.5rem",
                  lineHeight: "0.85",
                  color: "var(--gold-deep)",
                  fontWeight: 500,
                }}
              >
                T
              </span>
              ogether we will explore why doing more is not the answer, and
              why the freedom you have been searching for has been quietly
              waiting on the other side of effort. You will learn what it
              truly means to be, how to recognise when you are pushing against
              your own life, and the keys that allow you to soften, align and
              live from a deeper place. We will move through practical
              reflections to help you come home to yourself, and discover why
              becoming more of who you already are is the most powerful work
              you will ever do.
            </p>

            <p
              className="mt-10 font-serif italic text-lg md:text-xl text-center"
              style={{ color: "var(--gold-deep)" }}
            >
              A quiet, expansive hour. A return to who you really are.
            </p>
          </div>
        </div>

        {/* Three soft pillars beneath the card */}
        <div
          className="mt-16 md:mt-20 grid md:grid-cols-3 gap-10 md:gap-14 max-w-5xl mx-auto"
          data-testid="discover-pillars"
        >
          {PILLARS.map((p, i) => (
            <article
              key={p.roman}
              className="text-center fade-up px-2"
              style={{ animationDelay: `${200 + i * 120}ms` }}
              data-testid={`discover-pillar-${i + 1}`}
            >
              <p
                className="font-serif italic text-3xl"
                style={{ color: "var(--gold-deep)" }}
                aria-hidden="true"
              >
                {p.roman}
              </p>
              <h3
                className="mt-3 font-serif text-2xl md:text-[1.65rem] leading-snug"
                style={{ color: "var(--ink)" }}
              >
                {p.title}
              </h3>
              <p className="mt-4 text-sm md:text-base text-[color:var(--ink-soft)] leading-relaxed pretty">
                {p.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Discover;
